import { SchedulerEditSlot, SchedulerEditSlotProps, SchedulerSlot, SchedulerSlotProps } from '@progress/kendo-react-scheduler';
import { useCallback, useState, useMemo } from 'react';
import { LocationProviderDetailModel, ProcessedSchedules, Schedule, ScheduleType, UserProviderInfoDetailModel } from "../../../data/entities";
import { toClockTimeString, toTimeString, toYearMonthDayLocalString } from '../../../utils';

interface BlockedTimeSlotProps extends SchedulerSlotProps {
  schedules: Schedule[];
  providers: UserProviderInfoDetailModel[] | LocationProviderDetailModel[];
  isAllLocationsView?: boolean;
  onSlotContextMenu?: (e: React.MouseEvent, slot: { start: Date; providerId?: number }) => void;
}

interface BlockedTimeEditSlotProps extends SchedulerEditSlotProps {
  schedules: Schedule[];
  providers: UserProviderInfoDetailModel[] | LocationProviderDetailModel[];
  isAllLocationsView?: boolean;
}

// The stripped look when there is no availability
export const BlockedTimeSlot = (props: BlockedTimeSlotProps) => {

  const slotDate = props.start;
  const providerIds = getProviderIds(props);

  const scheduleMap = useProcessedSchedules(props.schedules, providerIds);
  const isAvailable = checkSlotAvailability(slotDate, scheduleMap, providerIds, props.isAllLocationsView);

  const handleContextMenu = props.onSlotContextMenu
    ? (e: React.MouseEvent) => {
        const groupValue = providerIds.length === 1 ? providerIds[0] : undefined;
        const matchedProvider = (props.providers as any[]).find(p => p.providerId === groupValue);
        props.onSlotContextMenu!(e, { start: props.start, providerId: matchedProvider?.providerId });
      }
    : undefined;

  const slotTime = toClockTimeString(props.zonedStart ?? props.start);

  return (
    <SchedulerSlot
      {...props}
      style={{
        ...props.style,
        position: 'relative',
        backgroundImage: isAvailable
          ? undefined
          : 'repeating-linear-gradient(45deg, transparent, transparent 10px, #d0d0d0 10px, #d0d0d0 20px)',
        //cursor: isAvailable ? 'pointer' : 'not-allowed',
        cursor: 'pointer',
        opacity: isAvailable ? 1 : 0.6,
      }}
    >
      <div
        className="slot-time-hint"
        data-slot-time={slotTime}
        style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 0 }}
        onContextMenu={handleContextMenu}
      />
    </SchedulerSlot>
  );
};

// Prevents interaction with blocked slots
export const BlockedTimeEditSlot = (props: BlockedTimeEditSlotProps) => {
  const [formItem, setFormItem] = useState(null);
  
  const slotDate = props.start;
  const providerIds = getProviderIds(props);
  
  const todayLocal = new Date();
  todayLocal.setHours(0, 0, 0, 0);
  const isPast = slotDate < todayLocal;

  const slotDuration = (props.end.getTime() - props.start.getTime()) / (1000 * 60 * 60);
  const isMonthView = slotDuration >= 24;
  
  const scheduleMap = useProcessedSchedules(props.schedules, providerIds);
  
  // Determine availability
  const isAvailable = isPast
    ? false // Block past dates
    : isMonthView 
      ? true // Allow month view clicks
      : checkSlotAvailability(slotDate, scheduleMap, providerIds, props.isAllLocationsView);

  const handleFormItemChange = useCallback(
    (event: any) => {
      if (isAvailable) {
        setFormItem(event.value);
      }
    },
    [isAvailable]
  );

  return (
    <SchedulerEditSlot 
      {...props} 
      formItem={formItem} 
      onFormItemChange={handleFormItemChange} 
    />
  );
};

/* Helper Functions */ 

const getProviderIds = (props: any): number[] => {
  if (props.group?.resources && props.group.resources.length > 0) {
    const resource = props.group.resources[0] as any;
    const groupValue = resource.value || resource.data?.value || resource.providerId || resource.locationId;
    if (groupValue) {
      return [groupValue];
    }
  }
  return props.providers.map((p: any) => p.providerId);
};

const checkSlotAvailability = (
  slotDate: Date,
  scheduleMap: Map<number, ProcessedSchedules>, 
  providerIds: number[],
  isAllLocationsView?: boolean  
): boolean => {

  if (isAllLocationsView) return true;

  const slotDateStr = toYearMonthDayLocalString(slotDate);
  const todayStr = toYearMonthDayLocalString(new Date());

  // Past dates always show as available (For clarity)
  if (slotDateStr < todayStr) return true;

  // Block everything if there is no schedules 
  if (scheduleMap.size === 0) return false; 
  
  // Check if any provider is available at this time
  return providerIds.some(providerId => {
    const processedSchedules = scheduleMap.get(providerId);
    if (!processedSchedules) return false;
    
    const activeOverride = processedSchedules.overrides.find(schedule => {
      
      const effectiveDate = typeof schedule.effectiveDate === 'string' 
        ? schedule.effectiveDate 
        : toYearMonthDayLocalString(schedule.effectiveDate);
      
      if (slotDateStr < effectiveDate) return false;
      
      const effectiveEndDate = schedule.effectiveEndDate
        ? typeof schedule.effectiveEndDate === 'string'
          ? schedule.effectiveEndDate
          : toYearMonthDayLocalString(schedule.effectiveEndDate)
        : null;
      
      return !effectiveEndDate || slotDateStr <= effectiveEndDate;
    });
    
    if (activeOverride) {
      if (!activeOverride.scheduleBlocks || activeOverride.scheduleBlocks.length === 0) {
        return false;
      }
      
      const dayOfWeek = slotDate.getDay();
      const timeStr = toTimeString(slotDate);
      
      const isAvailableInOverride = activeOverride.scheduleBlocks.some(block => 
        block.dayOfWeek === dayOfWeek &&
        timeStr >= block.startTime &&
        timeStr < block.endTime
      );

      return isAvailableInOverride;
    }
    
    const defaultSchedules = processedSchedules.defaults;
    
    const currentDefault = defaultSchedules.find(schedule => {
      const effectiveDate = typeof schedule.effectiveDate === 'string' 
        ? schedule.effectiveDate 
        : toYearMonthDayLocalString(schedule.effectiveDate);
      return slotDateStr >= effectiveDate; 
    });
    
    if (!currentDefault || !currentDefault.scheduleBlocks) return false;
    
    const dayOfWeek = slotDate.getDay();
    const timeStr = toTimeString(slotDate); 
        
    return currentDefault.scheduleBlocks.some(block => 
      block.dayOfWeek === dayOfWeek &&
      timeStr >= block.startTime &&
      timeStr < block.endTime
    );
  });
};

const useProcessedSchedules = (schedules: Schedule[], providerIds: number[]) => {
  return useMemo(() => {
    const scheduleMap = new Map<number, ProcessedSchedules>(); 
    
    providerIds.forEach(providerId => {
      const providerSchedules = schedules.filter(s => s.providerId === providerId);
      
      if (providerSchedules.length === 0) {
        scheduleMap.set(providerId, { defaults: [], overrides: [] }); 
        return;
      }
      
      // Get all default schedules and sort by most recent
      const defaultSchedules = providerSchedules
        .filter(s => s.scheduleType === ScheduleType.Default)
        .sort((a, b) => {
          const dateA = typeof a.effectiveDate === 'string' 
            ? a.effectiveDate 
            : toYearMonthDayLocalString(a.effectiveDate); 
          const dateB = typeof b.effectiveDate === 'string' 
            ? b.effectiveDate 
            : toYearMonthDayLocalString(b.effectiveDate); 
          return dateB.localeCompare(dateA);
        });
      
      // Get all override schedules and sort by most recent
      const overrideSchedules = providerSchedules
        .filter(s => s.scheduleType === ScheduleType.Override)
        .sort((a, b) => {
          const dateA = typeof a.effectiveDate === 'string' 
            ? a.effectiveDate 
            : toYearMonthDayLocalString(a.effectiveDate);
          const dateB = typeof b.effectiveDate === 'string' 
            ? b.effectiveDate 
            : toYearMonthDayLocalString(b.effectiveDate);
          return dateB.localeCompare(dateA);
        });
      
      scheduleMap.set(providerId, {
        defaults: defaultSchedules,
        overrides: overrideSchedules
      });
    });
    
    return scheduleMap;
  }, [schedules, providerIds.join(',')]); 
};